// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the one menu a dataset has, wherever it is listed (owner,
 * 2026-10-03: "right click opens a small menu"; 2026-11-03: playlists
 * retire, so it adds "to a new (or existing) Khipu").
 *
 * Three ways in, one menu: a right-click (or the keyboard's menu key, or
 * Shift+F10) on a row, a long press on a touch screen, and the row's "+"
 * button, which is also what a headset's controller reaches. It offers
 * Load, Open beside, Details, **Add to a new Khipu…**, **Add to a Khipu**
 * (the Khipus it can add to, with a tick where the dataset already is;
 * choosing a ticked one takes out the steps the menu added), and Share.
 *
 * Which Khipus, by plan (owner, 2026-11-03):
 * - the one open in the Khipu editor, for everyone; a change shows there
 *   at once and is kept when the editor saves or downloads it;
 * - a plan that keeps Khipus (`keepsKhipus`): its stories and briefings in
 *   My Khipus, each change saved to the account; "Add to a new Khipu…"
 *   names one and saves it there;
 * - signed out, Free and Plus keep none on the account: "Add to a new
 *   Khipu…" opens the new one in the editor (Download keeps it).
 *
 * Choosing a Khipu keeps the menu open, so the tick and the line under it
 * show what happened. Own data stays out of Khipus, as it stayed out of
 * playlists. {@link promptKhipuName} is the small in-app dialog for the
 * new Khipu's name.
 */

import { dataService } from '../services/dataService'
import { displayTitle } from '../services/datasetTaxonomy'
import { buildDatasetShareUrl, shareDataset } from '../services/shareService'
import { currentEntitlements, onAccountChange } from '../services/account'
import { newKhipuId, type Khipu } from '../services/khipu'
import { addDatasetStep, hasDataset, removeDatasetSteps, storyWithDataset } from '../services/khipuCollect'
import { forgetKeptList, lastKeptList, listKeptKhipus, readKeptKhipu, saveKhipu, type KhipuStoreError } from '../services/khipuStore'
import { activeKhipuEditor } from './khipuEditorUI'
import { keepsKhipus } from './myLessons'
import { getLocale, t } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'

export interface DatasetMenuActions {
  /** Load the dataset; the menu offers Load only when this is given. */
  onLoad?: (id: string) => void
  /** Show the dataset's details (expand its card); offered only when given. */
  onDetails?: (id: string) => void
  /** AYNI (multi-globe plan): open the dataset on a second globe beside the one shown; offered only when given. */
  onOpenBeside?: (id: string) => void
  announce?: (message: string) => void
}

/** What the page lends the menu for Khipus (main.ts). */
export interface DatasetMenuKhipuHost {
  /** Open a new Khipu, not saved anywhere yet, in the Khipu editor. */
  openInEditor: (k: Khipu, trigger: HTMLElement | null) => void
  announce?: (message: string) => void
}

let khipuHost: DatasetMenuKhipuHost | null = null

/** main.ts: how the menu opens the Khipu editor. */
export function setDatasetMenuKhipuHost(host: DatasetMenuKhipuHost | null): void {
  khipuHost = host
}

/** The editable kinds the menu adds to (the Khipu editor's kinds). */
const COLLECTING = new Set(['story', 'briefing'])

/** How long a finger must stay down for a long press, in ms. */
export const LONG_PRESS_MS = 500
/** How far a finger may drift before a press stops counting, in px. */
const LONG_PRESS_SLOP_PX = 10

const MENU_ID = 'dataset-menu'
interface OpenMenu {
  id: string
  trigger: HTMLElement | null
  actions: DatasetMenuActions
  /** What the last Khipu choice did, shown under the list. */
  status: { text: string; ok: boolean } | null
  /** A Khipu being saved (its id, or 'editor'). */
  busy: string | null
  /** Where it opened: a point, or the element it hangs under (and that element's box then, if it goes). */
  at: { x: number; y: number } | HTMLElement
  anchorBox: DOMRect | null
}
let openFor: OpenMenu | null = null
let openedAt = 0
let listFailed = false

function titleOf(id: string): string {
  const d = dataService.getDatasetById(id)
  return d ? displayTitle(d) : id
}

/** Whether the menu offers Khipus for this dataset: the plan has Khipus, and it is not own data. */
function offersKhipus(id: string): boolean {
  return currentEntitlements().khipus && dataService.getDatasetById(id)?.ownData !== true
}

function announce(message: string): void {
  ;(openFor?.actions.announce ?? khipuHost?.announce)?.(message)
}

function host(): HTMLElement {
  let el = document.getElementById(MENU_ID)
  if (!el) {
    el = document.createElement('div')
    el.id = MENU_ID
    el.className = 'dataset-menu hidden'
    el.setAttribute('role', 'menu')
    document.body.appendChild(el)
    el.addEventListener('click', onMenuClick)
    el.addEventListener('keydown', onMenuKeydown)
    document.addEventListener('pointerdown', (e) => {
      if (openFor && !el!.contains(e.target as Node) && !openFor.trigger?.contains(e.target as Node)) closeDatasetMenu()
    }, true)
    window.addEventListener('resize', () => closeDatasetMenu())
  }
  wireAccount()
  return el
}

/** The account and licence the kept list belongs to; undefined until wired. */
let accountKey: string | null | undefined

/** Another account or licence: another list of Khipus (wired once; the first call is the account as it is). */
function wireAccount(): void {
  if (accountKey !== undefined) return
  accountKey = null
  onAccountChange((s) => {
    const key = s.status === 'signedIn' ? `${s.email ?? ''}|${s.licence?.ref ?? ''}` : s.status
    if (key === accountKey) return
    const first = accountKey === null
    accountKey = key
    if (first) return
    forgetKeptList()
    listFailed = false
  })
}

function items(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(`#${MENU_ID} [role^="menuitem"]:not([aria-disabled="true"])`)]
}

const item = (action: string, label: string, extra = '') =>
  `<button type="button" role="menuitem" class="dataset-menu-item" data-action="${action}"${extra}>${escapeHtml(label)}</button>`

/** One Khipu in the list: a tick where the dataset is, and why a ticked one cannot be taken out. */
function khipuItem(menu: OpenMenu, action: 'toggle-editing' | 'toggle-kept', khipuId: string, name: string, inIt: boolean, removable: boolean): string {
  const blocked = inIt && !removable
  const tip = blocked ? t('datasetMenu.cannotRemove', { khipu: name }) : inIt ? t('datasetMenu.removeFrom', { khipu: name }) : t('datasetMenu.addTo', { khipu: name })
  const busy = menu.busy === khipuId
  return `<button type="button" role="menuitemcheckbox" aria-checked="${inIt}" class="dataset-menu-item dataset-menu-khipu" data-action="${action}" data-khipu="${escapeAttr(khipuId)}"`
    + ` title="${escapeAttr(tip)}"${blocked ? ' aria-disabled="true"' : ''}${busy ? ' aria-busy="true"' : ''}>`
    + `<span class="dataset-menu-check" aria-hidden="true">${inIt ? '&#x2713;' : ''}</span>`
    + `<span class="dataset-menu-khipu-name">${escapeHtml(action === 'toggle-editing' ? t('datasetMenu.editing', { khipu: name }) : name)}</span></button>`
}

/** The "Add to a Khipu" part: the Khipu being edited, then the ones kept on the account. */
function khipuGroupHtml(menu: OpenMenu): string {
  const id = menu.id
  const editor = activeKhipuEditor()
  const editing = editor?.isOpen() ? editor : null
  const rows: string[] = []
  if (editing) rows.push(khipuItem(menu, 'toggle-editing', 'editor', editing.title() || t('khipu.record.untitled'), editing.hasDataset(id), editing.canRemoveDataset(id)))
  let note = ''
  if (keepsKhipus()) {
    const list = lastKeptList()
    const editingId = editing?.khipu().id
    if (list && !list.readOnly) {
      for (const k of list.items) {
        if (!COLLECTING.has(k.kind) || k.id === editingId) continue
        // The list cannot tell whether the step is still plain; choosing it says so if not.
        rows.push(khipuItem(menu, 'toggle-kept', k.id, k.title, (k.datasets ?? []).includes(id), true))
      }
    } else if (!list) note = listFailed ? t('datasetMenu.listFailed') : t('datasetMenu.loading')
  }
  let html = `<div class="dataset-menu-heading" id="dataset-menu-existing">${escapeHtml(t('datasetMenu.addToExisting'))}</div>`
  if (rows.length) html += `<div class="dataset-menu-khipus" role="group" aria-labelledby="dataset-menu-existing">${rows.join('')}</div>`
  if (note) html += `<div class="dataset-menu-empty" role="menuitem" aria-disabled="true">${escapeHtml(note)}</div>`
  else if (!rows.length) html += `<div class="dataset-menu-empty" role="menuitem" aria-disabled="true">${escapeHtml(t('datasetMenu.noKhipus'))}</div>`
  if (menu.status) html += `<p class="dataset-menu-status${menu.status.ok ? '' : ' dataset-menu-status-error'}" role="status">${escapeHtml(menu.status.text)}</p>`
  return `<div class="dataset-menu-khipu-group">${html}</div>`
}

function render(menu: OpenMenu): string {
  const { id, actions } = menu
  let html = `<div class="dataset-menu-title" aria-hidden="true">${escapeHtml(titleOf(id))}</div>`
  if (actions.onLoad) html += item('load', t('datasetMenu.load'))
  if (actions.onOpenBeside) html += item('open-beside', t('datasetMenu.openBeside'))
  if (actions.onDetails) html += item('details', t('datasetMenu.details'))
  if (offersKhipus(id)) {
    html += `<div class="dataset-menu-sep" role="separator"></div>`
    html += item('new-khipu', t('datasetMenu.addToNew'))
    html += khipuGroupHtml(menu)
  }
  html += `<div class="dataset-menu-sep" role="separator"></div>`
  html += item('share', t('datasetMenu.share'))
  return html
}

/** Redraw the Khipu part only, keeping the focus on the same item. */
function redrawKhipus(): void {
  const el = document.getElementById(MENU_ID)
  const group = el?.querySelector('.dataset-menu-khipu-group')
  if (!el || !group || !openFor) return
  const focused = document.activeElement as HTMLElement | null
  const focusKey = focused && el.contains(focused) ? (focused.dataset.khipu ? `[data-khipu="${CSS.escape(focused.dataset.khipu)}"]` : focused.dataset.action ? `[data-action="${focused.dataset.action}"]` : null) : null
  group.outerHTML = khipuGroupHtml(openFor)
  // The list or the status line can make it taller or wider: placed again, so it stays in the window
  // (found on the live site 2026-11-03: long Khipu names arriving after "Loading…" ran it off the edge).
  reposition(openFor)
  if (focusKey) el.querySelector<HTMLElement>(focusKey)?.focus()
}

/** Place the menu for what it now holds, from where it opened. */
function reposition(menu: OpenMenu): void {
  const el = document.getElementById(MENU_ID)
  if (!el) return
  // From the corner first, so the menu takes its natural size before it is measured.
  el.style.top = '0px'
  el.style.left = '0px'
  const at = menu.at
  if (at instanceof HTMLElement) place(el, { x: 0, y: 0 }, at.isConnected ? at.getBoundingClientRect() : menu.anchorBox)
  else place(el, at, null)
}

/** Keep the menu inside the window: below/after the point when there is room, else above/before. */
function place(el: HTMLElement, at: { x: number; y: number }, anchor: DOMRect | null): void {
  const margin = 8
  const rect = el.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  let top = anchor ? anchor.bottom + 4 : at.y
  if (top + rect.height > vh - margin) top = Math.max(margin, (anchor ? anchor.top - 4 : at.y) - rect.height)
  let left = anchor ? (document.documentElement.dir === 'rtl' ? anchor.left : anchor.right - rect.width) : at.x
  left = Math.max(margin, Math.min(left, vw - rect.width - margin))
  el.style.top = `${Math.round(Math.max(margin, top))}px`
  // Screen coordinates, measured: physical left is right in both directions.
  el.style.left = `${Math.round(left)}px`
}

/**
 * Open the menu for a dataset, at a point (a right-click or press) or
 * under an element (the "+" button). Focus moves into the menu.
 */
export function openDatasetMenu(id: string, at: { x: number; y: number } | HTMLElement, actions: DatasetMenuActions = {}, status: OpenMenu['status'] = null): void {
  // The same button again closes it.
  if (openFor && openFor.id === id && at instanceof HTMLElement && openFor.trigger === at && !status) {
    closeDatasetMenu(true)
    return
  }
  const el = host()
  const trigger = at instanceof HTMLElement ? at : (document.activeElement as HTMLElement | null)
  const menu: OpenMenu = { id, trigger, actions, status, busy: null, at, anchorBox: at instanceof HTMLElement ? at.getBoundingClientRect() : null }
  openFor = menu
  openedAt = Date.now()
  el.setAttribute('aria-label', t('datasetMenu.aria', { title: titleOf(id) }))
  el.innerHTML = render(menu)
  el.classList.remove('hidden')
  reposition(menu)
  if (trigger) trigger.setAttribute('aria-expanded', 'true')
  const focusAt = status ? el.querySelector<HTMLElement>('.dataset-menu-khipu[aria-checked="true"]') : null
  ;(focusAt ?? items()[0])?.focus()
  // The kept Khipus, fetched when not at hand; the list redraws when they come.
  if (offersKhipus(id) && keepsKhipus() && !lastKeptList()) {
    void listKeptKhipus().then((result) => {
      listFailed = !result.ok
      if (openFor === menu) redrawKhipus()
    })
  }
}

/** Close the menu, if open; focus goes back to what opened it when asked. */
export function closeDatasetMenu(restoreFocus = false): void {
  const el = document.getElementById(MENU_ID)
  if (!el || !openFor) return
  const trigger = openFor.trigger
  openFor = null
  el.classList.add('hidden')
  el.innerHTML = ''
  trigger?.setAttribute('aria-expanded', 'false')
  if (restoreFocus) trigger?.focus()
}

export function isDatasetMenuOpen(): boolean {
  return openFor !== null
}

function saveErrorText(error: KhipuStoreError, name: string): string {
  if (error === 'too_many') return t('lessons.player.saveTooMany')
  if (error === 'full') return t('lessons.player.saveFull')
  return t('datasetMenu.saveFailed', { khipu: name })
}

/** The Khipu being edited: add the dataset, or take out the steps the menu added. */
function toggleEditing(menu: OpenMenu): void {
  const editor = activeKhipuEditor()
  if (!editor?.isOpen()) return
  const name = editor.title() || t('khipu.record.untitled')
  const title = titleOf(menu.id)
  let text: string
  if (editor.hasDataset(menu.id)) {
    if (!editor.removeDataset(menu.id)) return
    text = t('datasetMenu.removed.announce', { title, khipu: name })
  } else {
    editor.addDataset(menu.id, title)
    text = t('datasetMenu.added.announce', { title, khipu: name })
  }
  menu.status = { text, ok: true }
  redrawKhipus()
  announce(text)
}

/** A Khipu kept on the account: read it, change it, save it back with its files. */
async function toggleKept(menu: OpenMenu, khipuId: string): Promise<void> {
  if (menu.busy) return
  const kept = lastKeptList()?.items.find((k) => k.id === khipuId)
  const name = kept?.title ?? ''
  menu.busy = khipuId
  menu.status = { text: t('datasetMenu.saving'), ok: true }
  redrawKhipus()
  const finish = (text: string, ok: boolean) => {
    menu.busy = null
    menu.status = { text, ok }
    if (openFor === menu) redrawKhipus()
    announce(text)
  }
  const read = await readKeptKhipu(khipuId)
  if (!read.ok) return finish(t('datasetMenu.saveFailed', { khipu: name }), false)
  const k = read.value.khipu
  const title = titleOf(menu.id)
  const removing = hasDataset(k, menu.id)
  const next = removing ? removeDatasetSteps(k, menu.id) : addDatasetStep(k, menu.id, title)
  if (!next) return finish(t('datasetMenu.cannotRemove', { khipu: name }), false)
  const saved = await saveKhipu({ ...next, modified: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z') }, read.value.files ?? [])
  if (!saved.ok) return finish(saveErrorText(saved.error, name), false)
  // The save forgot the list; fetch it again for the ticks.
  await listKeptKhipus()
  finish(t(removing ? 'datasetMenu.removed.announce' : 'datasetMenu.added.announce', { title, khipu: name }), true)
}

/** "Add to a new Khipu…": name it; kept on the account where the plan keeps Khipus, else opened in the editor. */
async function addToNew(id: string, trigger: HTMLElement | null, actions: DatasetMenuActions): Promise<void> {
  const keeps = keepsKhipus()
  const editor = activeKhipuEditor()
  const notes = [
    ...(keeps ? [] : [t('datasetMenu.newKhipu.notKept')]),
    ...(!keeps && editor?.isOpen() && editor.dirty() ? [t('datasetMenu.newKhipu.replaces')] : []),
  ]
  const name = await promptKhipuName({ title: t('datasetMenu.newKhipu.title'), initial: '', confirm: t('datasetMenu.newKhipu.create'), trigger, notes })
  if (!name) return
  const lang = getLocale()
  const title = titleOf(id)
  const k = storyWithDataset(newKhipuId(), name, lang, Date.now(), id, title)
  if (!keeps) {
    khipuHost?.openInEditor(k, trigger)
    return
  }
  const saved = await saveKhipu(k)
  const text = saved.ok ? t('datasetMenu.savedNew', { title, khipu: name }) : saveErrorText(saved.error, name)
  if (saved.ok) await listKeptKhipus()
  ;(actions.announce ?? khipuHost?.announce)?.(text)
  // Shown where it was asked, with the new Khipu ticked.
  if (trigger?.isConnected) openDatasetMenu(id, trigger, actions, { text, ok: saved.ok })
}

function onMenuClick(e: MouseEvent): void {
  const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]')
  if (!btn || !openFor) return
  const menu = openFor
  const { id, actions } = menu
  const action = btn.dataset.action
  if (action === 'toggle-editing' || action === 'toggle-kept') {
    if (btn.getAttribute('aria-disabled') === 'true') return
    if (action === 'toggle-editing') toggleEditing(menu)
    else void toggleKept(menu, btn.dataset.khipu ?? '')
    return
  }
  const trigger = menu.trigger
  closeDatasetMenu(action !== 'new-khipu')
  if (action === 'load') actions.onLoad?.(id)
  else if (action === 'open-beside') actions.onOpenBeside?.(id)
  else if (action === 'details') actions.onDetails?.(id)
  else if (action === 'share') {
    const dataset = dataService.getDatasetById(id)
    if (dataset) {
      void shareDataset({ title: dataset.title, text: t('tools.share.text', { title: dataset.title }), url: buildDatasetShareUrl(dataset) })
        .then((ok) => { if (ok) actions.announce?.(t('tools.announce.shared')) })
    }
  } else if (action === 'new-khipu') void addToNew(id, trigger, actions)
}

function onMenuKeydown(e: KeyboardEvent): void {
  const list = items()
  const at = list.indexOf(document.activeElement as HTMLElement)
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    const step = e.key === 'ArrowDown' ? 1 : -1
    list[(at + step + list.length) % list.length]?.focus()
  } else if (e.key === 'Home' || e.key === 'End') {
    e.preventDefault()
    list[e.key === 'Home' ? 0 : list.length - 1]?.focus()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    closeDatasetMenu(true)
  } else if (e.key === 'Tab') {
    closeDatasetMenu(false)
  }
}

/**
 * Give every row in `container` the menu: right-click, the menu key or
 * Shift+F10 on a focused row, and a long press on a touch screen.
 * `idOf` says which dataset a row is (null: not a dataset row).
 */
export function attachDatasetMenu(container: HTMLElement, idOf: (target: HTMLElement) => string | null, actions: DatasetMenuActions): void {
  if (container.dataset.datasetMenu === 'wired') return
  container.dataset.datasetMenu = 'wired'

  container.addEventListener('contextmenu', (e) => {
    const id = idOf(e.target as HTMLElement)
    if (!id) return
    e.preventDefault()
    // A long press on Android fires this too; the press timer may have opened it already.
    if (openFor?.id === id && Date.now() - openedAt < 800) return
    openDatasetMenu(id, { x: e.clientX, y: e.clientY }, actions)
  })

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'ContextMenu' && !(e.key === 'F10' && e.shiftKey)) return
    const target = e.target as HTMLElement
    const id = idOf(target)
    if (!id) return
    e.preventDefault()
    openDatasetMenu(id, target, actions)
  })

  let press: { id: string; x: number; y: number; timer: ReturnType<typeof setTimeout> } | null = null
  let swallowClick = false
  const cancel = () => {
    if (press) clearTimeout(press.timer)
    press = null
  }
  container.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return
    const id = idOf(e.target as HTMLElement)
    if (!id || (e.target as HTMLElement).closest('button, a, input')) return
    cancel()
    const { clientX: x, clientY: y } = e
    press = {
      id, x, y,
      timer: setTimeout(() => {
        press = null
        swallowClick = true
        openDatasetMenu(id, { x, y }, actions)
      }, LONG_PRESS_MS),
    }
  })
  container.addEventListener('pointermove', (e) => {
    if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > LONG_PRESS_SLOP_PX) cancel()
  })
  for (const type of ['pointerup', 'pointercancel', 'pointerleave', 'scroll']) container.addEventListener(type, cancel, { passive: true })
  // The click that ends a long press must not also expand or load the row.
  container.addEventListener('click', (e) => {
    if (!swallowClick) return
    swallowClick = false
    e.preventDefault()
    e.stopPropagation()
  }, true)
}

// ------------------------------------------------------------ name dialog

/**
 * Ask for a new Khipu's name in a small dialog over the page, with any
 * notes under the field. Resolves to the trimmed name (the default name
 * when left empty), or null when cancelled.
 */
export function promptKhipuName(opts: { title: string; initial: string; confirm: string; trigger?: HTMLElement | null; notes?: string[] }): Promise<string | null> {
  document.getElementById('khipu-name-dialog')?.remove()
  const backdrop = document.createElement('div')
  backdrop.id = 'khipu-name-dialog'
  backdrop.className = 'name-dialog-backdrop'
  const fallback = t('khipu.record.untitled')
  backdrop.innerHTML = `
    <form class="name-dialog" role="dialog" aria-modal="true" aria-labelledby="khipu-name-title">
      <h2 class="name-dialog-title" id="khipu-name-title">${escapeHtml(opts.title)}</h2>
      <label class="name-dialog-label" for="khipu-name-input">${escapeHtml(t('datasetMenu.newKhipu.label'))}</label>
      <input id="khipu-name-input" class="name-dialog-input" type="text" maxlength="120" autocomplete="off" value="${escapeAttr(opts.initial)}" placeholder="${escapeAttr(fallback)}">
      ${(opts.notes ?? []).map((n) => `<p class="name-dialog-note">${escapeHtml(n)}</p>`).join('')}
      <div class="name-dialog-actions">
        <button type="button" class="name-dialog-cancel">${escapeHtml(t('datasetMenu.newKhipu.cancel'))}</button>
        <button type="submit" class="name-dialog-ok">${escapeHtml(opts.confirm)}</button>
      </div>
    </form>`
  document.body.appendChild(backdrop)
  const form = backdrop.querySelector('form')!
  const input = backdrop.querySelector<HTMLInputElement>('#khipu-name-input')!
  input.focus()
  input.select()
  return new Promise((resolve) => {
    const finish = (value: string | null) => {
      backdrop.remove()
      opts.trigger?.focus()
      resolve(value)
    }
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const name = (input.value.trim() || fallback).slice(0, 120)
      finish(name)
    })
    backdrop.querySelector('.name-dialog-cancel')!.addEventListener('click', () => finish(null))
    backdrop.addEventListener('pointerdown', (e) => {
      if (e.target === backdrop) finish(null)
    })
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        finish(null)
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        const stops = [...form.querySelectorAll<HTMLElement>('input, button')]
        const i = stops.indexOf(document.activeElement as HTMLElement)
        const next = e.shiftKey ? (i <= 0 ? stops.length - 1 : i - 1) : (i + 1) % stops.length
        e.preventDefault()
        stops[next].focus()
      }
    })
  })
}
